import { CustomClaimValue, Device, DeviceCommands, Devices, DeviceStatus, DeviceStatusDisplayNames, getDeviceStatusDisplayName, HardwareStatuses, Icons, formatUptime, getAverageCpuUsage, HardwareStatusCardData } from "@app/shared";
import { ChangeEventHandler, useCallback, useMemo, useRef, useState } from "react";
import { useInterval } from "react-use";
import { DeviceCommandTable, DeviceConfigSection, EditDeviceDetails, IconLink, HardwareStatusCards } from ".";
import { compareVersions, toastError, toastSuccess } from "../../helpers";
import { useConfirmDialog, useFetch, useForwardedPortsHub } from "../../hooks";
import { useEditModelDialog } from "../../hooks/useEditModelDialog";
import { DeviceConfigMethods, DeviceConfigTabView } from "./DeviceConfigTabView";
import { DevicePortsTable } from "./DevicePortsTable";
import { HoverTooltip } from "./HoverTooltip";
import { DeviceStatusCharts } from "./DeviceStatusCharts";
import { Select } from "./inputs";
import { PageContainer } from "./PageContainer";


type DeviceStatusViewProps = {
    device: Device | undefined;
    onChange?: (device: Device) => void;
};

export const DeviceStatusView: React.FC<DeviceStatusViewProps> = ({ device, onChange }) => {
    const [commandsToShow, setCommandsToShow] = useState<number | "Pending">("Pending");
    const [portsToShow, setPortsToShow] = useState<number | "Open">("Open");
    const [deviceCommands, , deviceCommandHelper] = useFetch(() => (device ? Devices.getCommands(device?.id, commandsToShow === "Pending" ? 50 : (commandsToShow as number), commandsToShow === "Pending") : Promise.resolve(undefined)), [commandsToShow, device]);
    const [devicePorts, , devicePortsHelper] = useFetch(() => (device ? Devices.getForwardedPorts(device?.id, portsToShow === "Open" ? 50 : (portsToShow as number), portsToShow === "Open") : Promise.resolve(undefined)), [portsToShow, device]);
    const [statuses] = useFetch(() => (device ? HardwareStatuses.getRecentForDevice(device?.id, 1) : Promise.resolve(undefined)), [device]);
    const editDialog = useEditModelDialog(Device);
    const confirmDialog = useConfirmDialog();
    const activeTab = useRef("General" as DeviceConfigSection);
    const deviceConfigRef = useRef<DeviceConfigMethods>(null);
    //Interval to refresh the device commands
    useInterval(() => {
        deviceCommandHelper.refreshData();
    }, 10000);

    const handleForwardedPortsChanged = useCallback(() => {
        devicePortsHelper.refreshData();
    }, [devicePortsHelper]);
    useForwardedPortsHub(device?.id, handleForwardedPortsChanged);

    const handleShowCommandsChanged: ChangeEventHandler<HTMLSelectElement> = (e) => {
        if (e.target.value === "Pending") setCommandsToShow("Pending");
        else setCommandsToShow(parseInt(e.target.value));
    };
    const handleShowPortsChanged: ChangeEventHandler<HTMLSelectElement> = (e) => {
        if (e.target.value === "Open") setPortsToShow("Open");
        else setPortsToShow(parseInt(e.target.value));
    };

    const pendingCommandCount = useMemo(
        () => deviceCommands?.filter(c => !c.dateSentToDevice).length ?? 0,
        [deviceCommands]
    );

    const handleClearAllCommandsClick = () => {
        if (!device || pendingCommandCount === 0) return;
        confirmDialog.show(
            "Clear pending commands?",
            `Cancel all ${pendingCommandCount} pending command${pendingCommandCount === 1 ? "" : "s"} for this device?`,
            "Clear All",
            async () => {
                try {
                    await DeviceCommands.deleteAllPending(device.id);
                    toastSuccess("Cleared", "Pending device commands were cleared.");
                    deviceCommandHelper.refreshData();
                } catch (e) {
                    toastError("Unable to clear commands", e + "");
                }
            }
        );
    };

    const getSecondsBetweenDates = (past: Date, future: Date) => {
        const ms = future.getTime() - past.getTime();
        return ms / 1000.0;
    };

    const latestStatus = statuses?.[statuses.length - 1];

    const statusCardData = useMemo((): HardwareStatusCardData | null => {
        if (!device) return null;

        const isDisconnected = device.status === DeviceStatus.LostConnection || device.status === DeviceStatus.PartialConnection;

        return {
            status: device.status ? (getDeviceStatusDisplayName(device.status) ?? "") : "",
            statusHoverText: getStatusHoverText(device),
            cpuUsage: getAverageCpuUsage(latestStatus?.coreUsages),
            uptime: latestStatus?.upTime,
            cellSignal: latestStatus?.signal ?? undefined,
            humidity: latestStatus?.humidity,
            isDisconnected,
            lastHeartbeat: isDisconnected && device.lastHeartbeat
                ? formatUptime(getSecondsBetweenDates(device.lastHeartbeat, new Date()))
                : undefined,
        };
    }, [device, latestStatus]);

    const portForwardingHeartbeatStale = useMemo(() => {
        if (!device) return false;
        // Older firmware does not emit port-forwarding heartbeats; suppress for them.
        if (compareVersions(device.firmwareVersion, "1.4.0.0") <= 0) return false;
        if (!device.lastPortForwardingCheck) return true;
        const ageMs = Date.now() - new Date(device.lastPortForwardingCheck).getTime();
        return ageMs > 10 * 60 * 1000;
    }, [device]);

    return (
        <>
            {statusCardData && <HardwareStatusCards data={statusCardData} />}
            <div className="mb-6">
                <DeviceStatusCharts device={device} />
                <PageContainer
                    className="min-h-[570px]"
                    Icon={Icons.Settings}
                    title="Configuration"
                    titleRight={
                        <IconLink Icon={Icons.Edit} onClick={() => deviceConfigRef.current?.openDialog()}>
                            Edit
                        </IconLink>
                    }
                >

                    <DeviceConfigTabView ref={deviceConfigRef} device={device} onChange={onChange} claimsForRadar={CustomClaimValue.SuperAdmin} />
                </PageContainer>
            </div>
            <PageContainer
                Icon={Icons.Message}
                title="Forwarded Ports"
                titleRight={
                    <div className="flex justify-center items-center gap-3">
                        {portForwardingHeartbeatStale && (
                            <HoverTooltip hoverable={
                                <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">
                                    [No Heartbeat]
                                </span>
                            }>
                                Port forwarding service has not checked in for over 10 minutes. Expected when a port is currently open to the device.
                            </HoverTooltip>
                        )}
                        <Select
                            options={[
                                { label: "Open", value: "Open" },
                                { label: "Last 50", value: 50 },
                                { label: "Last 100", value: 100 },
                            ]}
                            value={portsToShow}
                            onChange={handleShowPortsChanged}
                        />
                    </div>
                }
            >
                <DevicePortsTable ports={devicePorts} isLoading={devicePortsHelper.isLoading} onPortsChanged={handleForwardedPortsChanged} />
            </PageContainer>
            <PageContainer
                Icon={Icons.Message}
                title="Device Commands"
                titleRight={
                    <div className="flex justify-center items-center gap-3">
                        {pendingCommandCount > 0 && (
                            <IconLink Icon={Icons.Trash} onClick={handleClearAllCommandsClick}>
                                Clear All
                            </IconLink>
                        )}
                        <Select
                            options={[
                                { label: "Pending", value: "Pending" },
                                { label: "Last 50", value: 50 },
                                { label: "Last 100", value: 100 },
                            ]}
                            value={commandsToShow}
                            onChange={handleShowCommandsChanged}
                        />
                    </div>
                }
            >
                <DeviceCommandTable commands={deviceCommands} isLoading={deviceCommandHelper.isLoading && !deviceCommandHelper.isRefreshing} onCommandChanged={() => deviceCommandHelper.refreshData()} />
            </PageContainer>
            {
                editDialog.renderModal(
                    (model, helpers, onSubmit, onCancel) => (
                        <EditDeviceDetails initialSection={activeTab.current} onSubmit={onSubmit} onCancel={onCancel} device={model} helpers={helpers} />
                    ),
                    "w-full max-w-5xl rounded-2xl"
                )
            }
            {confirmDialog.renderDialog()}
        </>
    );
};

export const getStatusHoverText = (d: Device | undefined) => {
    const status = DeviceStatusDisplayNames[d?.status ?? 0];
    if (d?.lastStatusChange) {
        const locale = navigator.languages?.[0] ?? "en-US";
        const time = new Intl.DateTimeFormat(locale, { dateStyle: "short", timeStyle: "long" }).format(d.lastStatusChange);
        if (d.lastStatusChange > new Date(2020, 0, 1)) {
            return (
                <>
                    <span className="font-semibold">{status}</span>
                    <span>{" as of " + time}</span>
                    {!!d.statusMessage?.length && <div>{d.statusMessage}</div>}
                </>
            );
        }
    }
    return <span className="font-semibold">{status}</span>;
};
